import {
  ActionIcon, Dialog, Group, Text, Image, Tooltip, UnstyledButton, useMantineTheme, Flex, Modal
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconLogout2, IconAdjustmentsCheck, IconArrowLeft, IconLayoutSidebarLeftCollapseFilled, IconDownload
} from '@tabler/icons-react';

import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useCallback, useEffect, useState } from 'react';

import DownloadProposal from '../Modals/DownloadProposal';
import { type Proposal } from '../../types/apiTypes';
import { ProposalStatusDropdown } from '../DropdownMenus';
import EditorSideMenu from '../EditorSideMenu';
import Logo from '../../assets/ensis-logo-icon.svg';
import { type SectionData } from '../../Pages/Editor/SectionEditor';

import { getProposalManagementRoute } from '../../utils/routerUtils';

import { getSplitLocation } from '../../utils/stringUtils';
import UserAvatarMenu from '../DropdownMenus/UserAvatarMenu';
import { defaultModalProps } from '../../utils/mantineUtils';

interface Props {
  proposal: Proposal
  sideMenuLocked: boolean
  setSideMenuLocked: (locked: boolean) => void
  sections: SectionData[]
  showMenuOnHover: boolean
  setShowMenuOnHover: (showHovered: boolean) => void
}

const EditorHeader: React.FC<Props> = (props: Props) => {
  const { proposal, sideMenuLocked, setSideMenuLocked, setShowMenuOnHover, sections, showMenuOnHover } = props;
  const [searchParams] = useSearchParams();
  const downloadOpen = searchParams.get('download') ?? false;
  const { organization } = useParams();
  const { colors, other: colorVars } = useMantineTheme();
  const location = useLocation();
  const isProposalManagement = getSplitLocation(location).includes('manage');
  const navigate = useNavigate();
  const [downloadProposalOpened, downloadProposalHandlers] = useDisclosure(downloadOpen === 'true');

  const [opened, setOpened] = useState(false);

  const handleExitProposal = () => {
    // Navigate to the proposals list or a specific route
    navigate(`/org/${organization}/proposals`);
  };
  const handleBackArrowClick = useCallback(() => {
    // confirms that user has a previous route to navigate to
    if (isProposalManagement && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(`/org/${organization}/proposals`);
    }
  }, [isProposalManagement, window.history.length, organization]);

  const backArrow = (
    <Tooltip label='Return to proposal'>
      <ActionIcon
        variant='subtle'
        onClick={handleBackArrowClick}
      >
        <IconArrowLeft color={colorVars.primaryIconColor} />
      </ActionIcon>
    </Tooltip>
  );

  useEffect(() => {
    if (isProposalManagement) {
      setOpened(false);
    }
  }, [isProposalManagement]);
  const toggleLockedSideMenu = useCallback(() => {
    setSideMenuLocked(!sideMenuLocked);
    if (!sideMenuLocked) {
      setOpened(false);
    }
  }, [sideMenuLocked, setSideMenuLocked]);
  const handleToggleProposalManagement = useCallback(() => {
    navigate(getProposalManagementRoute(organization, proposal.uid));
  }, []);

  return (
    <>
      <Modal opened={downloadProposalOpened} {...defaultModalProps} size={750}>
        <DownloadProposal
          proposalUid={proposal.uid}
          onClose={downloadProposalHandlers.close}
        />
      </Modal>
      <Dialog
        transitionProps={{ transition: opened ? 'slide-right' : undefined, duration: opened ? 200 : 10 }}
        position={{ top: 4, left: 4 }}
        p={0}
        opened={opened}
      >
        <Flex
          w='500px'
          h='calc(100vh - 10px)'
          align={'stretch'}
          justify='stretch'
          styles={{
            root: {
              borderRadius: 4,
              boxShadow: '2px 3px 4px 0px #00000033'
            }
          }}
          onMouseLeave={() => { setOpened(false); }}
        >
          <EditorSideMenu
            p='4px'
            style={{
              borderRadius: 4
            }}
            tooltipLabel='Lock outline open'
            toggleLockedSideMenu={toggleLockedSideMenu}
            hasEditPermission={true}
            sections={sections}
          />
        </Flex>
      </Dialog >
      <Group align='center' h={41} mt={4} mx={8} justify="space-between">
        <Group gap={8} align='center'>
          {isProposalManagement
            ? backArrow
            : (!sideMenuLocked &&
              <ActionIcon
                onMouseEnter={() => {
                  if (showMenuOnHover) {
                    setOpened(true);
                  }
                }}
                onClick={() => { setOpened(true); }}
                onMouseLeave={() => { setShowMenuOnHover(true); }}
                m={0}
                color={colors.gray[4]}
                variant='outline'
                style={{
                  borderRadius: 4,
                  backgroundColor: colors.lightPurple[0]
                }} size={32}>
                <IconLayoutSidebarLeftCollapseFilled color={colorVars.primaryIconColor} />
              </ActionIcon>)
          }
          <UnstyledButton onClick={handleExitProposal}>
            <Image src={Logo} fit='contain' width='auto' />
          </UnstyledButton>
          <Text fz='sm' fw={500}>
            {proposal.name}
          </Text>
          <ProposalStatusDropdown proposalUid={proposal.uid} />
        </Group>
        <Group gap={12}>
          {!isProposalManagement && <Tooltip
            label='Proposal Management'
          >
            <ActionIcon variant='subtle' onClick={handleToggleProposalManagement}>
              <IconAdjustmentsCheck color={colorVars.primaryIconColor} />
            </ActionIcon>
          </Tooltip>}
          <Tooltip label='Exit proposal'>
            <ActionIcon variant='subtle' onClick={handleExitProposal} >
              <IconLogout2 color={colorVars.primaryIconColor} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label='Download Proposal'>
            <ActionIcon size={28} variant='subtle' onClick={downloadProposalHandlers.open}>
              <IconDownload color={colorVars.primaryIconColor} />
            </ActionIcon>
          </Tooltip>
          <UserAvatarMenu />
        </Group>
      </Group >
    </>
  );
};

export default EditorHeader;
